/**
 * CompanyContactActions — Phase 6
 * Adds "Message" and "Book Appointment" buttons to public company profile
 * pages. Additive: rendered by PublicCompanyPage; nothing existing is removed.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Calendar, X } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { commsAPI, BOOKING_KINDS } from "../../lib/comms";

export default function CompanyContactActions({ business }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showMsg, setShowMsg] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);

  // Booking form state
  const [kind, setKind] = useState("test_drive");
  const [requestedAt, setRequestedAt] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [notes, setNotes] = useState("");

  const requireLogin = () => {
    if (!user) {
      toast.error(lang === "ar" ? "يرجى تسجيل الدخول أولاً" : "Please log in first");
      navigate("/login");
      return true;
    }
    return false;
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (requireLogin()) return;
    if (!msgText.trim()) return;
    setSending(true);
    try {
      await commsAPI.sendMessage({
        business_id: business.id,
        kind: "customer_dealer",
        text: msgText.trim(),
      });
      toast.success(lang === "ar" ? "تم إرسال الرسالة" : "Message sent");
      setMsgText("");
      setShowMsg(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "فشل الإرسال" : "Failed"));
    } finally {
      setSending(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (requireLogin()) return;
    if (!requestedAt) return toast.error(lang === "ar" ? "اختر الوقت" : "Pick a time");
    setSending(true);
    try {
      await commsAPI.createBooking({
        kind,
        business_id: business.id,
        requested_at: new Date(requestedAt).toISOString(),
        customer_name: name,
        customer_phone: phone,
        notes,
      });
      toast.success(lang === "ar" ? "تم إرسال طلب الحجز" : "Booking requested");
      setShowBook(false);
      setRequestedAt(""); setNotes("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "فشل الحجز" : "Failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="company-contact-actions">
      <button
        type="button"
        onClick={() => (requireLogin() ? null : setShowMsg(true))}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold text-[#0a0a0a] shadow-sm"
        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
        data-testid="btn-contact-message"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        {lang === "ar" ? "مراسلة الشركة" : "Message"}
      </button>
      <button
        type="button"
        onClick={() => (requireLogin() ? null : setShowBook(true))}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold border border-[#0a0a0a]/15 bg-white hover:border-[#c9a55c] hover:text-[#8a6f2e]"
        data-testid="btn-contact-book"
      >
        <Calendar className="w-3.5 h-3.5" />
        {lang === "ar" ? "حجز موعد" : "Book Appointment"}
      </button>

      {/* Message modal */}
      {showMsg && (
        <>
          <button type="button" aria-label="close" onClick={() => setShowMsg(false)}
                  className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-4 top-24 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md z-50 rounded-2xl bg-white shadow-2xl p-5"
               data-testid="msg-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">
                {lang === "ar" ? `مراسلة ${business.name_ar}` : `Message ${business.name_en || business.name_ar}`}
              </h3>
              <button type="button" onClick={() => setShowMsg(false)} className="text-[#8a8a8a]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={sendMessage} className="space-y-3">
              <textarea
                rows={4}
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                placeholder={lang === "ar" ? "اكتب رسالتك هنا…" : "Type your message…"}
                className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm focus:outline-none focus:border-[#c9a55c]"
                data-testid="msg-modal-text"
                required
              />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowMsg(false)} className="px-4 py-2 rounded-full text-sm text-[#525252]">
                  {lang === "ar" ? "إلغاء" : "Cancel"}
                </button>
                <button type="submit" disabled={sending || !msgText.trim()}
                        className="px-4 py-2 rounded-full text-sm font-bold text-[#0a0a0a] disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
                        data-testid="msg-modal-send">
                  {sending ? "…" : (lang === "ar" ? "إرسال" : "Send")}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Booking modal */}
      {showBook && (
        <>
          <button type="button" aria-label="close" onClick={() => setShowBook(false)}
                  className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-4 top-16 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:max-w-lg z-50 rounded-2xl bg-white shadow-2xl p-5 max-h-[92vh] overflow-y-auto"
               data-testid="book-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">
                {lang === "ar" ? `حجز موعد مع ${business.name_ar}` : `Book with ${business.name_en || business.name_ar}`}
              </h3>
              <button type="button" onClick={() => setShowBook(false)} className="text-[#8a8a8a]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submitBooking} className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-1">
                  {lang === "ar" ? "نوع الموعد" : "Appointment type"}
                </label>
                <select value={kind} onChange={(e) => setKind(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white"
                        data-testid="book-kind">
                  {BOOKING_KINDS.map((k) => (
                    <option key={k.key} value={k.key}>{lang === "ar" ? k.ar : k.en}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-1">
                  {lang === "ar" ? "الوقت المفضل" : "Preferred time"}
                </label>
                <input type="datetime-local" value={requestedAt} onChange={(e) => setRequestedAt(e.target.value)}
                       className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm"
                       data-testid="book-time" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-1">
                    {lang === "ar" ? "الاسم" : "Name"}
                  </label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm"
                         data-testid="book-name" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-1">
                    {lang === "ar" ? "الجوال" : "Phone"}
                  </label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                         className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" dir="ltr"
                         data-testid="book-phone" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-1">
                  {lang === "ar" ? "ملاحظات" : "Notes"}
                </label>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm"
                          data-testid="book-notes" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setShowBook(false)}
                        className="px-4 py-2 rounded-full text-sm text-[#525252]">
                  {lang === "ar" ? "إلغاء" : "Cancel"}
                </button>
                <button type="submit" disabled={sending}
                        className="px-4 py-2 rounded-full text-sm font-bold text-[#0a0a0a] disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
                        data-testid="book-submit">
                  {sending ? "…" : (lang === "ar" ? "إرسال الطلب" : "Send request")}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
